import React, { useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Grid from '@mui/material/Grid';
import { Box, Typography } from '@mui/material';
import { Alert } from 'components/atoms/Alert';
import { useTranslations } from 'hooks/useTranslations';
import { ERouteLinks } from 'models/route';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { useAlert } from 'models/alertContext';
import { useNavigate } from 'react-router-dom';
import { FormInput } from 'components/molecules/FormInput';
import { ILicenceAuthorityFormValues } from 'models/licenceAuthority';
import { StyledValueFormHeader } from '../styles';
import { FormSelect } from 'components/molecules/FormSelect';
import { FileDropzoneContainer, IFilePrev } from 'components/molecules/FormInputFile';
import { EMIMETypes } from 'constants/MIMETypes';
import useLicenceAuthorityFormSchema from './schemaValidation';
import { useLicenceAuthorities } from '../hooks';

interface ILicenceAuthoritiesForm {
  initialFormValues: ILicenceAuthorityFormValues;
  isSubmitLoading: boolean;
  onSubmit: (data: ILicenceAuthorityFormValues) => void;
  isEdit?: boolean;
  initFile?: IFilePrev;
}

const LicenceAuthorityForm = ({
  initialFormValues,
  isSubmitLoading,
  onSubmit,
  isEdit = false,
  initFile,
}: ILicenceAuthoritiesForm) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { samePageAlert } = useAlert();
  const [initFileIsDelete, setInitFileIsDelete] = useState(false);
  const { getSignatureFileValidate } = useLicenceAuthorities();

  const { schema } = useLicenceAuthorityFormSchema({
    isEdit: isEdit,
    initFileIsDelete: initFileIsDelete,
  });

  const deleteInitFile = () => setInitFileIsDelete(true);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ILicenceAuthorityFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<ILicenceAuthorityFormValues>,
  });

  const handleBack = () => {
    navigate(ERouteLinks.LicenceAuthorities);
  };

  const validateSignatureFile = async (
    file: File,
  ): Promise<{ errorMessage: string | null; warningMessages: string[] | null }> => {
    return await getSignatureFileValidate(file);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        {samePageAlert && (
          <Box sx={{ mt: 2 }}>
            <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
          </Box>
        )}
        <Grid container size={{ xs: 12 }} sx={{ rowGap: 3 }} columnSpacing="20px">
          <Grid size={{ xs: 12 }}>
            <StyledValueFormHeader>
              {t('licenceAuthorities.generalInformation.label')}
            </StyledValueFormHeader>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormInput
              name="name"
              control={control}
              errors={errors}
              label={t('licenceAuthorities.licenceAuthorityName.label')}
              setValue={setValue}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormSelect
              options={[
                { label: t('general.active.label'), value: 'true' },
                { label: t('general.inactive.label'), value: 'false' },
              ]}
              name="isActive"
              setValue={setValue}
              label={t('licenceAuthorities.status.label')}
              control={control}
              errors={errors}
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <StyledValueFormHeader>
              {t('licenceAuthorities.licenceAuthoritySignature.label')}
            </StyledValueFormHeader>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2">
              {t('licenceAuthorities.licenceAuthoritySignature.text')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FileDropzoneContainer
              label={t('licenceAuthorities.licenceAuthoritySignature.label')}
              name="signatureAttachmentFile"
              setValue={setValue}
              allowedType={[EMIMETypes.PNG]}
              maxSizeKB={500}
              required
              showPreviewFile
              showValidationInMB={false}
              errors={errors}
              initValue={initFile}
              deleteInitFile={deleteInitFile}
              additionalValidate={validateSignatureFile}
              allowedTypeIsDisabled={[EMIMETypes.JPEG, EMIMETypes.JPG]}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormInput
                name="authorizedPersonName"
                control={control}
                errors={errors}
                label={t('licenceAuthorities.authorizedPersonName.label')}
                setValue={setValue}
                required
              />
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormInput
                name="position"
                control={control}
                errors={errors}
                label={t('licenceAuthorities.position.label')}
                setValue={setValue}
                required
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container columnSpacing="20px" sx={{ rowGap: 1, mt: '40px' }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              label={t('general.cancel.button')}
              id="cancel"
              variant={EButtonVariants.outlined}
              onClick={handleBack}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              type="submit"
              fullWidth
              label={t('general.save.button')}
              id="save"
              variant={EButtonVariants.contained}
              isLoading={isSubmitLoading}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default LicenceAuthorityForm;
